import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import ActionDrawer from '../components/shared/ActionDrawer';
import { showSuccess, showError, showServerError } from '../../utils/toastHelper';

function CompanyForm({ isOpen, onClose, company, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'ACTIVE',
    metadata: {}
  });
  const [originsText, setOriginsText] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        description: company.description || '',
        status: company.status || 'ACTIVE',
        metadata: company.metadata || {}
      });
      setOriginsText((company.metadata?.origin || []).join('\n'));
    } else {
      setFormData({
        name: '',
        description: '',
        status: 'ACTIVE',
        metadata: {}
      });
      setOriginsText('');
    }
    setErrors({});
  }, [company, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Şirket adı zorunludur';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const isEdit = !!company;
      const url = isEdit ? `/companies/${company.id}` : '/companies';
      const method = isEdit ? 'PUT' : 'POST';

      const origins = originsText
        .split('\n')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      const payload = {
        ...formData,
        metadata: {
          ...formData.metadata,
          origin: origins
        }
      };

      const response = await apiRequest(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.success) {
        showSuccess(isEdit ? 'Şirket başarıyla güncellendi' : 'Şirket başarıyla oluşturuldu');
        onSuccess(true);
      } else {
        throw new Error(response.message || `Şirket ${isEdit ? 'güncelleme' : 'oluşturma'} işlemi başarısız oldu`);
      }
    } catch (error) {
      console.error('Company save failed:', error);
      if (error.isAuthError) return;
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || `Şirket ${company ? 'güncelleme' : 'oluşturma'} işlemi başarısız oldu`);
      }
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div className="d-flex justify-content-end gap-2 w-100">
      <button
        type="button"
        className="btn btn-light"
        onClick={() => onClose()}
        disabled={loading}
      >
        İptal
      </button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={loading}
        onClick={handleSubmit}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {company ? 'Güncelleniyor...' : 'Oluşturuluyor...'}
          </>
        ) : (
          company ? 'Güncelle' : 'Oluştur'
        )}
      </button>
    </div>
  );

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={() => onClose()}
      title={company ? 'Şirket Düzenle' : 'Yeni Şirket Ekle'}
      width="600px"
      footer={footer}
    >
      <div className="company-form-content">
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-light bg-opacity-50 border-0">
            <h6 className="mb-0 fw-bold text-dark">Şirket Bilgileri</h6>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label className="form-label text-muted small fw-medium">Şirket Adı <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Şirket adı giriniz"
                disabled={loading}
                required
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label text-muted small fw-medium">Açıklama</label>
              <textarea
                className="form-control"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Şirket hakkında açıklama"
                disabled={loading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label text-muted small fw-medium">Durum</label>
              <select
                className="form-select"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="ACTIVE">Aktif</option>
                <option value="INACTIVE">Pasif</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label text-muted small fw-medium">İzin Verilen URL'ler (Origin)</label>
              <textarea
                className="form-control"
                rows={3}
                value={originsText}
                onChange={(e) => setOriginsText(e.target.value)}
                placeholder={"https://subscription.kito.pet\nhttps://main.dtmas1dgkq8vt.amplifyapp.com\nhttp://localhost:3000"}
                disabled={loading}
              />
              <div className="form-text">
                Kullanıcıların bu şirkete kayıt olabilmesi için frontend URL'lerini her satıra bir tane olacak şekilde girin.
              </div>
            </div>
          </div>
        </div>

        {company && (
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-light bg-opacity-50 border-0">
              <h6 className="mb-0 fw-bold text-dark">Ek bilgiler</h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6 col-12">
                  <div className="border rounded p-3 bg-light h-100">
                    <div className="small text-muted mb-1">Oluşturma Tarihi</div>
                    <div className="fw-medium">{new Date(company.createdAt).toLocaleString('tr-TR')}</div>
                  </div>
                </div>
                <div className="col-md-6 col-12">
                  <div className="border rounded p-3 bg-light h-100">
                    <div className="small text-muted mb-1">Son Güncelleme</div>
                    <div className="fw-medium">{new Date(company.updatedAt).toLocaleString('tr-TR')}</div>
                  </div>
                </div>
                {company.subscriptionCount !== undefined && (
                  <div className="col-md-6 col-12">
                    <div className="border rounded p-3 bg-light h-100">
                      <div className="small text-muted mb-1">Abonelik Sayısı</div>
                      <div className="fw-bold text-primary">{company.subscriptionCount}</div>
                    </div>
                  </div>
                )}
                {company.activeUserCount !== undefined && (
                  <div className="col-md-6 col-12">
                    <div className="border rounded p-3 bg-light h-100">
                      <div className="small text-muted mb-1">Aktif Kullanıcı Sayısı</div>
                      <div className="fw-bold text-success">{company.activeUserCount}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </ActionDrawer>
  );
}

export default CompanyForm;
